<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '../api/client'
import type { Taxonomy } from '../api/types'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import ImportDialog from '../components/ImportDialog.vue'
import { IconUpload } from '../components/Icons'
import { Table, Td, Th, Tr } from '../components/Table'
import { Button, EmptyState, ErrorState, Panel, SkeletonRows, Badge } from '../components/ui'
import { formatDate } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

useTitle('剧')
const router = useRouter()
const importOpen = ref(false)
const { data, error, initial, reload } = useAsync(() => api.listShows())
const taxonomies = useAsync(() => api.listTaxonomies(), () => [])

const nameBySlug = computed(() => {
  const map = new Map<string, string>()
  for (const t of taxonomies.data.value?.taxonomies ?? []) map.set(`${t.kind}:${t.slug}`, t.name_zh)
  return map
})

function labelOf(kind: Taxonomy['kind'], slug: string): string {
  return nameBySlug.value.get(`${kind}:${slug}`) ?? slug
}

const shows = computed(() => data.value?.shows ?? [])
const totals = computed(() =>
  shows.value.reduce(
    (acc, s) => ({ episodes: acc.episodes + s.episode_count, subtitles: acc.subtitles + s.subtitle_count }),
    { episodes: 0, subtitles: 0 },
  ),
)

function onImportDone(payload: { showId: number; focusSeason?: number; partial?: boolean }) {
  importOpen.value = false
  reload()
  const query: Record<string, string> = {}
  if (payload.focusSeason != null) query.season = String(payload.focusSeason)
  if (payload.partial) query.partial = '1'
  void router.push({ path: `/shows/${payload.showId}`, query })
}
</script>

<template>
  <PageHeader title="剧" :subtitle="shows.length > 0 ? `${shows.length} 部 · ${totals.episodes} 集 · ${totals.subtitles} 条字幕` : undefined">
    <template #actions>
      <Button size="sm" variant="primary" @click="importOpen = true">
        <IconUpload :size="13" />
        导入
      </Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel>
      <ErrorState v-if="error" :message="error.message" @retry="reload" />
      <SkeletonRows v-else-if="initial" :rows="6" :cols="5" />
      <EmptyState v-else-if="shows.length === 0" title="还没有剧">
        <template #action>
          <Button size="sm" variant="primary" @click="importOpen = true">导入</Button>
        </template>
      </EmptyState>
      <Table v-else>
        <template #head>
          <Tr>
            <Th class="w-[88px]">ID</Th>
            <Th>题材</Th>
            <Th class="w-[72px]">语言</Th>
            <Th class="w-[72px]">季</Th>
            <Th class="w-[72px]">集</Th>
            <Th class="w-[88px]">字幕</Th>
            <Th class="w-[120px]">创建</Th>
          </Tr>
        </template>
        <Tr v-for="s in shows" :key="s.id" clickable @click="router.push(`/shows/${s.id}`)">
          <Td mono class="font-medium text-text">#{{ s.id }}</Td>
          <Td>
            <span
              v-if="(s.genres?.length ?? 0) === 0 && (s.tropes?.length ?? 0) === 0"
              class="text-dim"
            >—</span>
            <span v-else class="flex flex-wrap gap-1">
              <Badge v-for="g in s.genres ?? []" :key="g" tone="accent">{{ labelOf('genre', g) }}</Badge>
              <Badge v-for="t in (s.tropes ?? []).slice(0, 3)" :key="t" tone="info">{{ labelOf('trope', t) }}</Badge>
              <span
                v-if="(s.tropes?.length ?? 0) > 3"
                class="text-[11px] text-dim"
              >+{{ (s.tropes?.length ?? 0) - 3 }}</span>
            </span>
          </Td>
          <Td mono class="text-muted">{{ s.locale_count }}</Td>
          <Td mono>{{ s.season_count }}</Td>
          <Td mono>{{ s.episode_count }}</Td>
          <Td mono>{{ s.subtitle_count }}</Td>
          <Td class="text-muted whitespace-nowrap">{{ formatDate(s.created_at) }}</Td>
        </Tr>
      </Table>
    </Panel>
  </PageBody>

  <ImportDialog
    :open="importOpen"
    @close="importOpen = false"
    @done="onImportDone"
  />
</template>
